import { SubFundSummaryData } from '../../../../api/sub-fund-setup/types';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';

export interface SubFundSummaryBasicInfoData {
  internalFundName: string;
  externalFundName: string;
  baseCurrency: string;
  complianceRules: string;
  globalExposureMetric: string;
  globalExposureMetricBenchmarks?: string[];
}

export function generateColumnsForTable(columnParams: [string, string][]) {
  return columnParams.map((el) => ({
    field: el[0],
    title: el[1],
  }));
}


export function applyStylesToTableColumns<T extends object>(
  styleDictionary: Partial<
    Record<keyof T, { cellStyle?: any; headerStyle?: any }>
  >,
  columns: CustomColumn<T>[],
): CustomColumn<T>[] {
  return columns.map((col: CustomColumn<T>) => {
    if (
      col.field !== undefined &&
      typeof col.field === 'string' &&
      col.field in styleDictionary
    ) {
      return {
        ...col,
        ...styleDictionary[col.field as keyof T],
      };
    } else {
      return col;
    }
  });
}

export function getSubFundSummaryBasicInfoColumns(
  showBenchmarkColumn: boolean,
): CustomColumn<SubFundSummaryBasicInfoData>[] {
  const centeredTopCell = {
    textAlign: 'center' as const,
    verticalAlign: 'top' as const,
  };

  const columnDefs: [string, string][] = [
    ['internalFundName', 'Fund ID'],
    ['externalFundName', 'Fund Name'],
    ['baseCurrency', 'Currency'],
    ['complianceRules', 'Compliance Rules'],
    ['globalExposureMetric', 'Global Exposure Metric'],
  ];

  if (showBenchmarkColumn) {
    columnDefs.push(['globalExposureMetricBenchmarks', 'Benchmarks']);
  }

  return applyStylesToTableColumns<SubFundSummaryBasicInfoData>(
    {
      internalFundName: {
        headerStyle: { textAlign: 'center' },
        cellStyle: centeredTopCell,
      },
      externalFundName: {
        headerStyle: { textAlign: 'center' },
        cellStyle: centeredTopCell,
      },
      baseCurrency: {
        headerStyle: { textAlign: 'center' },
        cellStyle: centeredTopCell,
      },
      complianceRules: {
        headerStyle: { textAlign: 'center' },
        cellStyle: centeredTopCell,
      },
      globalExposureMetric: {
        headerStyle: { textAlign: 'center' },
        cellStyle: centeredTopCell,
      },
      globalExposureMetricBenchmarks: {
        headerStyle: { textAlign: 'center' },
        cellStyle: centeredTopCell,
      },
    },
    generateColumnsForTable(columnDefs),
  ).map((col) =>
    col.field === 'globalExposureMetricBenchmarks'
      ? {
        ...col,
        render: (rowData: SubFundSummaryBasicInfoData) => {
          const benchmarks = rowData.globalExposureMetricBenchmarks ?? [];

          if (!benchmarks.length) {
            return '—';
          }

          return (
            <>
              {benchmarks.map((benchmark, index) => (
                <div
                  key={`${benchmark}-${index}`}
                  style={
                    index > 0
                      ? {
                        marginTop: '0.5rem',
                        paddingTop: '0.5rem',
                        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                      }
                      : undefined
                  }
                >
                  {benchmark}
                </div>
              ))}
            </>
          );
        },
      }
      : col,
  );
}

export interface SubFundSummaryCounterpartyTable {
  counterparty: string;
  counterpartyType: string;
}

export interface SubFundSummaryRiskComplianceRulesTable {
  rule: string;
  value: string;
}

export interface SubFundSummaryStressTestTable {
  stressTest: string;
}

export const subFundSummaryRiskComplianceColumns =
  applyStylesToTableColumns<SubFundSummaryRiskComplianceRulesTable>(
    {
      rule: {
        headerStyle: { textAlign: 'center' },
        cellStyle: { textAlign: 'center' },
      },
      value: {
        headerStyle: { textAlign: 'center' },
        cellStyle: { textAlign: 'center' },
      },
    },
    generateColumnsForTable([
      ['rule', 'Rule'],
      ['value', 'Value'],
    ]),
  );

export const stressTestColumns =
  applyStylesToTableColumns<SubFundSummaryStressTestTable>(
    {
      stressTest: {
        headerStyle: { textAlign: 'center' },
        cellStyle: { textAlign: 'center' },
      },
    },
    generateColumnsForTable([['stressTest', 'Stress Test']]),
  );

export const subFundSummaryCounterpartyColumns = generateColumnsForTable([
  ['counterparty', 'Counterparty'],
  ['counterpartyType', 'Counterparty Type'],
]);

// ============ GENERATE DATA

export function generateData<TReturn>(
  data: SubFundSummaryData | undefined,
  callback: (data: SubFundSummaryData, ...args: any[]) => TReturn[],
  ...theArgs: any[]
): TReturn[] {
  if (!data) return [];
  return callback(data, ...theArgs);
}

export function generateBasicInfo(data: SubFundSummaryData) {
  const {
    base_currency: baseCurrency,
    external_fund_name: externalFundName,
    internal_fund_name: internalFundName,
    global_exposure_metric: globalExposureMetric,
    compliance_rules: complianceRules,
    var_approach: varApproach,
    global_exposure_metric_benchmark: globalExposureMetricBenchmark,
  } = data;

  const benchmarks =
    varApproach === 'relative' && globalExposureMetricBenchmark?.length
      ? globalExposureMetricBenchmark
      : undefined;

  return [
    {
      baseCurrency,
      externalFundName,
      internalFundName,
      globalExposureMetric,
      complianceRules,
      ...(benchmarks ? { globalExposureMetricBenchmarks: benchmarks } : {}),
    },
  ];
}

export function generateStressTestInfo(
  data: SubFundSummaryData,
  arg: 'historical' | 'relative' | 'risk_sector',
) {
  const { stress_test_data: stressTestData } = data;
  return stressTestData[arg].map((el) => ({
    stressTest: el,
  }));
}

export function generateRiskComplianceRulesInfo(data: SubFundSummaryData) {
  const { risk_compliance: rcRules } = data;

  const toReturn = rcRules.map(([rule, value]) => ({
    rule,
    value,
  }));

  // get rid of header row
  toReturn.shift();
  return toReturn;
}

export function generateCounterpartyInfo(data: SubFundSummaryData) {
  const { counterparty_data: cpData } = data;

  const toReturn = cpData.map(([counterparty, counterpartyType]) => ({
    counterparty,
    counterpartyType,
  }));
  toReturn.shift();
  return toReturn;
}
